// ─────────────────────────────────────────────────────────────────────────
// Tablero — "Mi tablero": los frentes abiertos de Maxi en una sola pantalla.
//
// Panel de control en tres columnas (una sola en móvil):
//   · Franja superior: contadores que llevan a su sección y chips de cuentas conectadas.
//   · Hoy: los 7 de la semana, lo que solo Maxi resuelve en minutos y lo que vence.
//   · Por área: tarjetas con contadores; al tocar una se ve su detalle.
//   · Seguimiento: pestañas Esperando, Congelados y Cerrados.
// En móvil "Hoy" parte abierto y el resto plegado; se recuerdan las secciones abiertas,
// el área elegida y la pestaña (localStorage, si el navegador lo permite).
//
// La disciplina la pone el servidor: si ya hay 7 en la semana o se alcanzó el tope de
// activos, responde 409 y acá se muestra el mensaje tal cual. No se esquiva desde la
// pantalla. Es personal: el servidor responde 403 a quien no sea dueño.
// ─────────────────────────────────────────────────────────────────────────
import React, { useEffect, useState, useCallback, useContext, useRef } from "react";
import { Navigate } from "react-router-dom";
import backend from "../../backend";
import AuthContext from "../../AuthContext";
import useTitle from "../../hooks/useTitle";
import useIsMobile from "../../hooks/useIsMobile";
import { C, CERRADOS, boton, campo, diaDe, diasEntre, fechaLarga, Seccion, leerPref, guardarPref } from "./componentes";
import FormFrente from "./FormFrente";
import HoyPanel from "./HoyPanel";
import AreasResumen, { SIN_MOVIMIENTO } from "./AreasResumen";
import CuentasChips, { useCuentas } from "./CuentasChips";
import Seguimiento, { PESTANAS } from "./Seguimiento";

const DIA = 86400000;
const ABIERTAS_INICIO = { hoy: true, areas: false, seguimiento: false };

/** Contador de la franja superior: al tocarlo lleva (o filtra) a su sección. */
const Contador = ({ n, rotulo, rojo, onClick }) => (
  <button
    className="chip-touch t-02"
    onClick={onClick}
    style={{ display: "inline-flex", alignItems: "baseline", gap: 6, minHeight: 34, padding: "4px 12px", borderRadius: 8, border: `1px solid ${rojo && n > 0 ? C.rojo : C.borde}`, background: C.caja, color: C.texto, cursor: "pointer", whiteSpace: "nowrap" }}
  >
    <span style={{ fontSize: 18, fontWeight: 700, fontVariantNumeric: "tabular-nums", color: rojo && n > 0 ? C.rojo : C.texto }}>{n}</span>
    <span style={{ fontSize: 12, color: C.suave }}>{rotulo}</span>
  </button>
);

/** Columna del panel: en escritorio con título fijo; en móvil, sección plegable recordada. */
const Columna = ({ id, clase, titulo, n, esMovil, abierta, onToggle, children }) => (
  <div id={id} className={`tab-col ${clase}`} style={{ scrollMarginTop: 80 }}>
    {esMovil ? (
      <Seccion titulo={titulo} n={n} plegable abierta={abierta} onToggle={onToggle} primera>
        {children}
      </Seccion>
    ) : (
      <>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>{titulo}</div>
        {children}
      </>
    )}
  </div>
);

const Tablero = () => {
  useTitle("Mi tablero");
  const authContext = useContext(AuthContext);
  const esAdmin = authContext?.can?.("administrar_usuarios") || false; // solo admin (patrón Cuadrilla.js)
  const esMovil = useIsMobile();
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState("");
  const [aviso, setAviso] = useState("");
  const [panel, setPanel] = useState(null); // { id, tipo: cerrar | nota | editar, valor }
  const [deshacer, setDeshacer] = useState(null); // { id, titulo, estadoAnterior }
  const [formAbierto, setFormAbierto] = useState(false);
  const [abiertas, setAbiertas] = useState(() => ({ ...ABIERTAS_INICIO, ...leerPref("abiertas", {}) }));
  // Área elegida o SIN_MOVIMIENTO; un valor guardado que no sea texto se ignora.
  const [seleccion, setSeleccion] = useState(() => {
    const a = leerPref("area", null);
    return typeof a === "string" ? a : null;
  });
  const [hojaArea, setHojaArea] = useState(false); // móvil: hoja de detalle del área
  const [pestana, setPestana] = useState(() => {
    const p = leerPref("pestana", "esperando");
    return PESTANAS.includes(p) ? p : "esperando";
  });
  const temporizador = useRef(null);
  const cuentas = useCuentas(esAdmin); // estado y polling viven acá, no en la hoja

  const cargar = useCallback(async () => {
    try {
      const res = await backend.get("tablero");
      setDatos(res.data);
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || "No se pudo cargar el tablero");
    }
  }, []);
  useEffect(() => { if (esAdmin) cargar(); }, [cargar, esAdmin]);
  useEffect(() => () => clearTimeout(temporizador.current), []);

  useEffect(() => guardarPref("abiertas", abiertas), [abiertas]);
  useEffect(() => guardarPref("area", seleccion), [seleccion]);
  useEffect(() => guardarPref("pestana", pestana), [pestana]);

  // Toda acción pasa por acá: si el servidor rechaza (409 = regla de disciplina) el mensaje se muestra tal cual.
  const ejecutar = async fn => {
    try {
      await fn();
      setAviso("");
      await cargar();
      return true;
    } catch (err) {
      setAviso(err.response?.data?.error || "No se pudo completar la acción");
      return false;
    }
  };
  const patch = (id, cuerpo) => ejecutar(() => backend.patch(`tablero/${id}`, cuerpo));

  if (!esAdmin) return <Navigate to="/dashboard" replace />;
  if (!datos && error) {
    return (
      <div className="tab-wrap" style={{ color: C.rojo }}>
        {error}
        <div style={{ marginTop: 12 }}><button style={boton()} onClick={cargar}>Reintentar</button></div>
      </div>
    );
  }
  if (!datos) return <div className="tab-wrap" style={{ color: C.suave }}>Cargando…</div>;

  const { frentes, resumen, tope, hoy } = datos;
  const abiertos = frentes.filter(f => !CERRADOS.includes(f.estado));
  const semana = frentes.filter(f => f.estado === "esta_semana").sort((a, b) => a.orden - b.orden);
  const cortas = abiertos.filter(f => f.accionCorta && ["activo", "esta_semana"].includes(f.estado));
  const activos = abiertos.filter(f => f.estado === "activo");
  const esperando = abiertos.filter(f => f.estado === "espera_tercero");
  const congelados = abiertos.filter(f => f.estado === "congelado");
  const cerrados = frentes.filter(f => CERRADOS.includes(f.estado));
  const vencenPronto = abiertos
    .filter(f => f.plazo && diasEntre(hoy, diaDe(f.plazo)) <= 7)
    .sort((a, b) => new Date(a.plazo) - new Date(b.plazo));
  // Misma regla que el contador del servidor (sinMovimiento14d), para poder listarlos.
  const limite = Date.now() - 14 * DIA;
  const sinMovimiento = abiertos.filter(
    f => f.estado !== "congelado" && f.ultimoMovimiento && new Date(f.ultimoMovimiento).getTime() <= limite
  );
  const nHoy = new Set([...semana, ...cortas, ...vencenPronto].map(f => f._id)).size;

  const cerrarCorta = async f => {
    // Solo se muestra "Cerrado" si el servidor confirmó el cambio.
    const ok = await patch(f._id, { estado: "cerrado", motivo: "Hecho" });
    if (!ok) return;
    setDeshacer({ id: f._id, titulo: f.titulo, estadoAnterior: f.estado });
    clearTimeout(temporizador.current);
    temporizador.current = setTimeout(() => setDeshacer(null), 5000);
  };
  const deshacerCierre = async () => {
    if (!deshacer) return;
    const { id, estadoAnterior } = deshacer;
    setDeshacer(null);
    await patch(id, { estado: estadoAnterior || "activo" });
  };

  const renderPanel = f => {
    if (!panel || panel.id !== f._id) return null;
    const rotulo = { cerrar: "Motivo del cierre (opcional)", nota: "Nota", editar: "Siguiente acción" }[panel.tipo];
    const confirmar = async () => {
      const v = panel.valor || "";
      let ok = true;
      if (panel.tipo === "cerrar") ok = await patch(f._id, { estado: "cerrado", motivo: v });
      if (panel.tipo === "nota" && v.trim()) ok = await ejecutar(() => backend.post(`tablero/${f._id}/nota`, { texto: v }));
      if (panel.tipo === "editar") ok = await patch(f._id, { siguienteAccion: v });
      if (ok) setPanel(null);
    };
    return (
      <div style={{ marginTop: 10 }}>
        <div style={{ fontSize: 13, color: C.suave, marginBottom: 4 }}>{rotulo}</div>
        <textarea
          rows={2}
          autoFocus
          style={{ ...campo, fontFamily: "inherit" }}
          value={panel.valor}
          onChange={e => setPanel({ ...panel, valor: e.target.value })}
        />
        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          <button style={boton(C.verde, true)} onClick={confirmar}>{panel.tipo === "cerrar" ? "Confirmar cierre" : "Guardar"}</button>
          <button style={boton()} onClick={() => setPanel(null)}>Cancelar</button>
        </div>
      </div>
    );
  };
  const abrirPanel = (f, tipo) =>
    setPanel({ id: f._id, tipo, valor: tipo === "editar" ? f.siguienteAccion || "" : "" });

  // Navegación desde la franja: abre la sección (móvil) y la lleva a la vista.
  const abrir = (clave, v) => setAbiertas(p => ({ ...p, [clave]: v }));
  const irA = (id, clave) => {
    if (clave) abrir(clave, true);
    setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
  };
  const elegirArea = a => {
    setSeleccion(a);
    setHojaArea(esMovil && !!a);
  };

  return (
    <div className="tab-wrap">
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <h1 style={{ fontSize: 24, margin: 0 }}>Mi tablero</h1>
          <div style={{ color: C.suave, textTransform: "capitalize", fontSize: 14 }}>{fechaLarga(hoy)}</div>
        </div>
        {!esMovil && (
          <button style={boton(C.azul, true)} onClick={() => setFormAbierto(true)}>+ Frente</button>
        )}
      </div>

      <div className="tab-franja chips-scroll" style={{ marginTop: 12 }}>
        <Contador n={semana.length} rotulo={`esta semana (de ${tope.semana})`} onClick={() => irA("tab-semana", "hoy")} />
        <Contador n={activos.length + semana.length} rotulo="activos" onClick={() => { elegirArea(null); irA("tab-col-areas", "areas"); }} />
        <Contador n={resumen.vencidos} rotulo="vencidos" rojo onClick={() => irA("tab-vencidos", "hoy")} />
        <Contador n={esperando.length} rotulo="esperando a terceros" onClick={() => { setPestana("esperando"); irA("tab-col-seguimiento", "seguimiento"); }} />
        <Contador n={resumen.sinMovimiento14d} rotulo="sin movimiento 14 d" onClick={() => { elegirArea(SIN_MOVIMIENTO); irA("tab-col-areas", "areas"); }} />
        <span aria-hidden style={{ width: 1, alignSelf: "stretch", background: C.borde, margin: "0 4px" }} />
        <CuentasChips {...cuentas} esMovil={esMovil} />
      </div>

      {cuentas.aviso && (
        <div style={{ marginTop: 12, padding: 12, borderRadius: 10, border: `1px solid ${C.ambar}`, color: C.ambar }} onClick={() => cuentas.setAviso("")}>
          {cuentas.aviso}
        </div>
      )}

      {error && (
        <div style={{ marginTop: 12, padding: 12, borderRadius: 10, border: `1px solid ${C.rojo}`, color: C.rojo, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          <span>{error}</span>
          <button style={boton()} onClick={cargar}>Reintentar</button>
        </div>
      )}

      {aviso && (
        <div style={{ marginTop: 12, padding: 12, borderRadius: 10, border: `1px solid ${C.ambar}`, color: C.ambar }} onClick={() => setAviso("")}>
          {aviso}
        </div>
      )}

      <div className="tab-grid">
        <Columna id="tab-col-hoy" clase="tab-col-1" titulo="Hoy" n={nHoy} esMovil={esMovil} abierta={!!abiertas.hoy} onToggle={v => abrir("hoy", v)}>
          <HoyPanel
            hoy={hoy}
            semana={semana}
            topeSemana={tope.semana}
            cortas={cortas}
            vencenPronto={vencenPronto}
            renderPanel={renderPanel}
            abrirPanel={abrirPanel}
            patch={patch}
            cerrarCorta={cerrarCorta}
          />
        </Columna>

        <Columna id="tab-col-areas" clase="tab-col-2" titulo="Por área" n={abiertos.length} esMovil={esMovil} abierta={!!abiertas.areas} onToggle={v => abrir("areas", v)}>
          <AreasResumen
            abiertos={abiertos}
            sinMovimiento={sinMovimiento}
            hoy={hoy}
            seleccion={seleccion}
            hojaMovil={hojaArea}
            esMovil={esMovil}
            onSeleccionar={elegirArea}
            onCerrarHoja={() => setHojaArea(false)}
            patch={patch}
            aviso={aviso}
          />
        </Columna>

        <Columna
          id="tab-col-seguimiento"
          clase="tab-col-3"
          titulo="Esperando, congelados y cerrados"
          n={esperando.length + congelados.length + cerrados.length}
          esMovil={esMovil}
          abierta={!!abiertas.seguimiento}
          onToggle={v => abrir("seguimiento", v)}
        >
          <Seguimiento
            hoy={hoy}
            esperando={esperando}
            congelados={congelados}
            cerrados={cerrados}
            pestana={pestana}
            onPestana={setPestana}
            patch={patch}
          />
        </Columna>
      </div>

      {deshacer && (
        <div
          style={{
            position: "fixed",
            left: 16,
            right: 16,
            bottom: esMovil ? "calc(146px + env(safe-area-inset-bottom))" : 24,
            zIndex: 1075,
            maxWidth: 728,
            margin: "0 auto",
            background: C.toast,
            border: `1px solid ${C.borde}`,
            borderRadius: 12,
            padding: 12,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            boxShadow: C.sombra,
          }}
        >
          <span style={{ fontSize: 14 }}>Cerrado: {deshacer.titulo}</span>
          <button style={boton(C.azul, true)} onClick={deshacerCierre}>Deshacer</button>
        </div>
      )}

      {esMovil && (
        <button
          onClick={() => setFormAbierto(true)}
          style={{ position: "fixed", right: 16, bottom: "calc(76px + env(safe-area-inset-bottom))", zIndex: 1075, minHeight: 56, padding: "0 22px", borderRadius: 28, border: "none", background: C.azul, color: C.texto, fontSize: 17, fontWeight: 600, boxShadow: C.sombra, cursor: "pointer" }}
        >
          + Frente
        </button>
      )}

      {formAbierto && (
        <FormFrente
          onCerrar={() => setFormAbierto(false)}
          onCrear={async cuerpo => {
            const ok = await ejecutar(() => backend.post("tablero", cuerpo));
            if (ok) setFormAbierto(false);
            return ok;
          }}
          aviso={aviso}
        />
      )}
    </div>
  );
};

export default Tablero;
